<script setup lang="ts">
import BaseButton from '@/components/BaseButton.vue'
import BaseInput from '@/components/BaseInput.vue'
import BaseSelect from '@/components/BaseSelect.vue'
import { supabase } from '@/composables/supabaseClient'
import toastThrowError from '@/helpers/toastThrowError'
import uuidv7 from '@/helpers/uuidv7'
import router from '@/router'
import { useDatabaseStore } from '@/stores/databaseStore'
import type { Tables } from '@/types/database.types'
import { computed, ref } from 'vue'

const props = defineProps<{ subdivisionId: string }>()

const databaseStore = useDatabaseStore()
const itemData = ref<Omit<Tables<'item'>, 'tenant_id'>>({
  accounting_code: null,
  equipment_productivity_per_hr: null,
  equipment_type_id: null,
  id: '',
  code: '',
  item_division_id: props.subdivisionId ?? 0,
  labor_productivity_per_hr: null,
  labor_type_id: null,
  name: '',
  uom_id: '',
  is_archived: false,
  description: null,
})

const divisionCode = computed(() => {
  const subdivision = databaseStore.itemDivisions?.find((d) => d.id === props.subdivisionId)
  const division = databaseStore.itemDivisions?.find((d) => d.id == subdivision?.parent_division_id)
  return `${division?.code}.${subdivision?.code}`
})

async function addItem() {
  // TODO - Ensure inputs are valid before saving
  const result = await supabase
    .from('item')
    .insert({
      id: uuidv7(),
      item_division_id: props.subdivisionId!,
      code: itemData.value.code,
      accounting_code: itemData.value.accounting_code,
      name: itemData.value.name,
      uom_id: itemData.value.uom_id,
      labor_type_id: itemData.value.labor_type_id,
      labor_productivity_per_hr: itemData.value.labor_productivity_per_hr,
      equipment_type_id: itemData.value.equipment_type_id,
      equipment_productivity_per_hr: itemData.value.equipment_productivity_per_hr,
      attributes: {},
      is_archived: false,
    })
    .select()
    .single()
  if (result.error) throw toastThrowError('Unable to add item', result.error)
  databaseStore.items?.push(result.data)
  await router.replace({ name: 'DatabaseItem', params: { itemId: result.data.id } })
}
</script>

<template>
  <div class="flex h-full w-full flex-col gap-3 p-3">
    <div class="flex w-full items-center justify-between bg-gray-100 p-3 text-center">
      <h1 class="w-full">Add Item</h1>
      <button class="cursor-pointer rounded-md p-2 hover:bg-gray-200" @click="router.back()">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="size-6"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
    <BaseInput
      name="Code"
      v-model="itemData.code"
      :formatter="(v) => `${divisionCode}.${v}`"
    ></BaseInput>
    <BaseInput name="Name" v-model="itemData.name"></BaseInput>
    <BaseSelect
      :select-options="databaseStore.unitsOfMeasure"
      option-key="id"
      option-name="code"
      option-value="id"
      v-model="itemData.uom_id"
      name="Unit"
    ></BaseSelect>
    <BaseSelect
      :select-options="databaseStore.laborTypes"
      option-key="id"
      option-name="name"
      option-value="id"
      v-model="itemData.labor_type_id"
      name="Labor Type"
    ></BaseSelect>
    <BaseInput
      v-if="itemData.labor_type_id"
      v-model="itemData.labor_productivity_per_hr"
      name="Labor Productivity"
    ></BaseInput>
    <BaseSelect
      :select-options="databaseStore.equipmentTypes"
      option-key="id"
      option-name="name"
      option-value="id"
      v-model="itemData.equipment_type_id"
      name="Equipment Type"
    ></BaseSelect>
    <BaseInput
      v-if="itemData.equipment_type_id"
      v-model="itemData.equipment_productivity_per_hr"
      name="Equipment Productivity"
    ></BaseInput>
    <BaseButton @click="addItem()"> Add Item </BaseButton>
  </div>
</template>
